/**
 * Phase 20 / Task 20.1 — import wizard storage (migration 0101). Plan of record:
 * `.planning/phases/20-data-import/20-PLAN.md` task 20.1; data model in `20-CONTEXT.md`
 * §7; decisions DEC-250..DEC-257.
 *
 * - `import_mapping_profiles`: saved column mappings per entity family (master spec §80).
 * - `import_batches`: one row per upload — §80's `ImportLog`, widened with the dry-run
 *   flag, the file hash and the undo record.
 * - `import_rows`: staging, per-row validation findings and per-row outcome. §80 requires
 *   that "no database changes occur during preview", which is what staging buys.
 * - `import_id_maps`: source key → lodeX id. The audit trail, the dry-run/real-run diff,
 *   and — the load-bearing one — the re-run idempotency key.
 *
 * All four are tenant-scoped under RLS (USING + WITH CHECK on `app.current_tenant_id`).
 * Nothing reads or writes them yet: upload and parsing are task 20.2, the commit engine
 * is 20.6. Task 20.0 shipped the template catalog whose results these tables store.
 *
 * ⚠ `import_rows` HOLDS PII — driver names, emails, phones, CDL and TWIC numbers pass
 * through `raw`/`mapped` on their way to `users`/`drivers`. It needs the same retention
 * and deletion treatment as the live tables (master spec §73; CCPA deletion must reach
 * staging). The warning lives here because this is where the shape is decided.
 *
 * ANSWERED by task 20.6f / [[DEC-266]] / migration 0109:
 * `packages/api/src/services/import-staging-purge.ts` purges staging 7 days after a
 * batch completes (and 7 days after upload for a batch that never did), and a verified
 * CCPA request reaches it immediately. `staging_purged_at` below records it. Only
 * `import_rows` is ever deleted — see that column's note, and `import_id_maps.batch_id`,
 * whose SET NULL exists precisely so this purge is possible.
 * Scheduled since task 20.12 AC-6 / [[DEC-281]]: an EventBridge rule runs the age sweep
 * daily at 09:00 UTC as an ECS task (`purge:import-staging`), with a non-zero exit and a
 * CloudWatch alarm on failure. Closes [[OPEN-066]].
 */
import {
  boolean,
  char,
  check,
  index,
  integer,
  jsonb,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  unique,
  uniqueIndex,
  uuid,
  varchar,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { tenants, users } from './users.js';
import { appUserRole } from './enums.js';

const tenantIsolation = sql`tenant_id::text = current_setting('app.current_tenant_id', true)`;

/**
 * The fourteen import templates, as the `entity_family` value set.
 *
 * `text` + CHECK rather than a Postgres enum — our call, flagged in 20-PLAN.md rather
 * than taken silently. Master spec §80 specifies
 * `entity_type ENUM('OPERATORS','SITES',…)` and that list was already outgrown before
 * Phase 20 started: `hauler_wells`, `requestors`, `products` and `yards` did not exist
 * when it was written. A fifteenth template is a one-line ALTER on a text+CHECK column
 * where an enum needs a type rewrite.
 *
 * These are the `key` values of `ALL_TEMPLATES` in
 * `packages/api/src/import-templates/definitions.ts` (task 20.0). The list is duplicated
 * here rather than imported because `@lodex-pro/shared` cannot depend on the API package.
 *
 * `packages/api/test/integration/import-data-model.test.ts` pins the chain end to end:
 * `ALL_TEMPLATES` → the migration SQL's three CHECK constraints → the live database. So a
 * fifteenth template fails CI unless the migration is extended with it. This list is the
 * one link the pin cannot reach — it is here only so the Drizzle CHECK expression matches
 * the migration, and a drift between the two surfaces as a `drizzle-kit generate` diff.
 */
export const IMPORT_ENTITY_FAMILIES = [
  'CUSTOMERS',
  'YARDS',
  'TRUCKS',
  'TRAILERS',
  'DRIVERS',
  'SITES',
  'WELLS',
  'TANKS',
  'DISPOSAL_WELLS',
  'REQUESTORS',
  'SITE_CONTACTS',
  'PRODUCTS',
  'SERVICE_CHARGE_RATES',
  'CUSTOMER_RATES',
] as const;
export type ImportEntityFamily = (typeof IMPORT_ENTITY_FAMILIES)[number];

/** Where a batch's rows came from. The legacy track is deferred (20-CONTEXT.md §10). */
export const IMPORT_SOURCE_SYSTEMS = ['SPREADSHEET', 'LEGACY_PAELODE'] as const;
export type ImportSourceSystem = (typeof IMPORT_SOURCE_SYSTEMS)[number];

/** §80's `ImportLog` statuses plus `PREVIEW` and `UNDONE`. */
export const IMPORT_BATCH_STATUSES = [
  'PENDING',
  'VALIDATING',
  'PREVIEW',
  'IMPORTING',
  'COMPLETE',
  'FAILED',
  'CANCELLED',
  'UNDONE',
] as const;
export type ImportBatchStatus = (typeof IMPORT_BATCH_STATUSES)[number];

/**
 * The async commit's own states (migration 0110, [[DEC-282]]).
 *
 * DONE and FAILED are terminal for ONE ATTEMPT, not for the batch: FAILED is
 * deliberately re-claimable, because 20.6c resume makes a retry safe — it skips
 * every row that already has an outcome, so no row is ever written twice. DONE
 * is not, which is what stops a finished import being run again by a double
 * click.
 */
export const IMPORT_COMMIT_STATES = ['QUEUED', 'RUNNING', 'DONE', 'FAILED'] as const;
export type ImportCommitState = (typeof IMPORT_COMMIT_STATES)[number];

/** §80's taxonomy, kept verbatim. */
export const IMPORT_ROW_SEVERITIES = ['OK', 'WARNING', 'ERROR'] as const;
export type ImportRowSeverity = (typeof IMPORT_ROW_SEVERITIES)[number];

/**
 * What happened to one staged row.
 *
 * `LINKED` is its own outcome, not a flavour of `MATCHED`: a driver who already exists
 * under ANOTHER hauler gets a new `tenant_users` + `drivers` row re-using the existing
 * `users` row (DEC-252), and a reconciliation reporting that as `MATCHED` would hide
 * that a cross-tenant person was joined to this tenant.
 */
export const IMPORT_ROW_OUTCOMES = [
  'CREATED',
  'MATCHED',
  'UPDATED',
  'LINKED',
  'SKIPPED',
  'FAILED',
] as const;
export type ImportRowOutcome = (typeof IMPORT_ROW_OUTCOMES)[number];

/** `FAILED` is absent: a row that failed produced no `target_id` to map. */
export const IMPORT_ID_MAP_OUTCOMES = [
  'CREATED',
  'MATCHED',
  'UPDATED',
  'LINKED',
  'SKIPPED',
] as const;
export type ImportIdMapOutcome = (typeof IMPORT_ID_MAP_OUTCOMES)[number];

const familyCheck = sql.raw(
  `entity_family IN (${IMPORT_ENTITY_FAMILIES.map((f) => `'${f}'`).join(', ')})`,
);
const sourceSystemCheck = sql.raw(
  `source_system IN (${IMPORT_SOURCE_SYSTEMS.map((s) => `'${s}'`).join(', ')})`,
);

// ── import_mapping_profiles ─────────────────────────────────────────────────

export const importMappingProfiles = pgTable(
  'import_mapping_profiles',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    entityFamily: text('entity_family').notNull(),
    profileName: varchar('profile_name', { length: 100 }).notNull(),
    /**
     * Array of `{source, target}` pairs; `target: null` = "skip this column", matching
     * §80's own example payload. JSONB rather than a child table because the document is
     * read and replaced as a unit and never queried by element.
     */
    columnMappings: jsonb('column_mappings').notNull(),
    /**
     * SET NULL, not CASCADE: an offboarded dispatcher's saved mapping is still useful to
     * the tenant, and losing it would be a surprising side effect of deactivating a user.
     */
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    lastUsedAt: timestamp('last_used_at', { withTimezone: true }),
    useCount: integer('use_count').notNull().default(0),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    unique('import_mapping_profiles_name_key').on(t.tenantId, t.entityFamily, t.profileName),
    index('import_mapping_profiles_tenant_family_idx').on(t.tenantId, t.entityFamily),
    check('import_mapping_profiles_entity_family_check', familyCheck),
    check('import_mapping_profiles_use_count_nonneg', sql`use_count >= 0`),
    pgPolicy('import_mapping_profiles_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type ImportMappingProfile = typeof importMappingProfiles.$inferSelect;
export type NewImportMappingProfile = typeof importMappingProfiles.$inferInsert;

// ── import_batches ──────────────────────────────────────────────────────────

export const importBatches = pgTable(
  'import_batches',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /**
     * RESTRICT, not SET NULL: "who ran this import" is the audit answer this table exists
     * to give, and master spec §32 requires an audit trail on every mutation. Deactivate
     * the user instead — nothing in the product deletes a `users` row.
     */
    importedBy: uuid('imported_by')
      .notNull()
      .references(() => users.id, { onDelete: 'restrict' }),
    /**
     * The M2 hook (20-CONTEXT.md §4.2) — set when Paelode runs an import on a hauler's
     * behalf. Nullable and UNUSED in this phase: the selector is deferred, and the column
     * is cheaper here than in a second migration. Master spec §75's
     * `tenant_setup_wizard.section_N_admin_override` already anticipates the same flow.
     */
    performedOnBehalfOf: uuid('performed_on_behalf_of').references(() => users.id, {
      onDelete: 'set null',
    }),
    sourceSystem: text('source_system').notNull().default('SPREADSHEET'),
    fileName: varchar('file_name', { length: 255 }),
    fileSizeBytes: integer('file_size_bytes'),
    /**
     * SHA-256 of the uploaded bytes, so the preview can say "you already imported this
     * exact file on the 3rd" before the hauler re-runs it by accident.
     */
    fileSha256: char('file_sha256', { length: 64 }),
    entityFamilies: text('entity_families')
      .array()
      .notNull()
      .default(sql`'{}'`),
    status: text('status').notNull().default('PENDING'),
    /**
     * A dry run walks the identical classify-and-write path with `commit = false`, records
     * the outcome it WOULD have taken, and rolls back. Its report has to be diffable
     * against the real run's (20-CONTEXT.md §4.4), which is why this is a flag on the
     * batch rather than something inferred from the absence of writes.
     */
    isDryRun: boolean('is_dry_run').notNull().default(false),
    totalRows: integer('total_rows').notNull().default(0),
    rowsCreated: integer('rows_created').notNull().default(0),
    rowsUpdated: integer('rows_updated').notNull().default(0),
    rowsSkipped: integer('rows_skipped').notNull().default(0),
    rowsFailed: integer('rows_failed').notNull().default(0),
    errorSummary: jsonb('error_summary'),
    /** S3 key of the downloadable row-by-row result workbook (§80 step 4). */
    resultFileS3Key: varchar('result_file_s3_key', { length: 500 }),
    startedAt: timestamp('started_at', { withTimezone: true }),
    completedAt: timestamp('completed_at', { withTimezone: true }),
    /**
     * Set when a batch is undone. Undo removes only rows this batch CREATED, only while
     * nothing references them, and REFUSES — naming the blocking reference — for any row
     * since invoiced or referenced by a live job. It never cascades: migration 0092 is the
     * lesson, where a cascade silently destroyed `hauler_sequence_counters` among others.
     */
    undoneAt: timestamp('undone_at', { withTimezone: true }),
    undoneBy: uuid('undone_by').references(() => users.id, { onDelete: 'set null' }),
    /**
     * When this batch's `import_rows` staging was deleted under the PII
     * retention policy (migration 0109, [[DEC-266]]). NULL = never purged.
     *
     * Needed because an empty `import_rows` otherwise cannot say WHY it is
     * empty: a batch mid-wizard and a batch whose staging we deliberately
     * deleted look identical, and the commit path has to refuse the second with
     * a different sentence ("upload the file again") rather than resuming into
     * a `imported: 0, failed: 0` success for an import that did not happen.
     *
     * Nothing else is purged — not the batch, not `import_id_maps` (the re-run
     * idempotency key, and what undo reads), not one live row.
     */
    stagingPurgedAt: timestamp('staging_purged_at', { withTimezone: true }),
    /**
     * The async write's own lifecycle (migration 0110, [[DEC-282]]). NULL = this
     * batch's commit was never claimed.
     *
     * Beside `status` rather than inside it, and that is the load-bearing part.
     * `status` is the REAL import's lifecycle, stamped by
     * `import-commit/finalize.ts`, which holds the invariant that a dry run
     * stamps NOTHING — "a stamped batch would be a record of an import that did
     * not happen". Routing a dry run through a QUEUED/RUNNING `status` would
     * have forced a choice between letting a preview end up COMPLETE (the lie
     * that file exists to prevent) and making a dry run's progress invisible
     * (the feature). So a dry run stays PREVIEW the whole way through while
     * `commitState` reaches DONE, and both columns stay true.
     */
    commitState: text('commit_state'),
    /**
     * How long the current RUNNING claim is good for. The runner extends it
     * while it works; once it stops, the timestamp passes and the sweeper
     * re-claims.
     *
     * This is the entire crash-recovery story. A deploy rolls the service
     * mid-import: SIGTERM kills the task, the rows it already wrote are
     * committed (per-row top-level transactions, DEC-107), and what is left is a
     * batch reading RUNNING with nobody running it. Without a lease there is no
     * way to tell that from a live run, and the only safe policy would be never
     * to retry.
     */
    commitLeaseExpiresAt: timestamp('commit_lease_expires_at', { withTimezone: true }),
    /**
     * Which process holds the claim. Diagnostic, and what makes the
     * graceful-shutdown release safe: a task releases only its OWN claims, so a
     * rolling deploy cannot have the task going down release a claim the task
     * coming up just took.
     */
    commitClaimedBy: text('commit_claimed_by'),
    /**
     * The difference between a retry and a crash loop. A batch whose rows crash
     * the writer deterministically would be re-claimed every 30 seconds forever,
     * and every tick would be spent on it instead of the batches behind it.
     */
    commitAttempts: integer('commit_attempts').notNull().default(0),
    /**
     * The summary the synchronous response used to return, kept so the status
     * endpoint can answer after the request that started the run is gone.
     *
     * A convenience for a real run — the counts are on this row and the per-row
     * detail is in `import_rows` and the result workbook. For a DRY RUN it is
     * the only place the report exists at all: the outer transaction is
     * discarded, so its outcomes and skip reasons are gone the moment it ends.
     * An async dry run without this column would compute AC-6's diffable report
     * and then throw it away.
     */
    commitReport: jsonb('commit_report'),
    /**
     * UPDATE_ON_MATCH (the default, AC-4) or SKIP_EXISTING, as the hauler chose
     * it in the request.
     *
     * On the row because a DIFFERENT process consumes it. Synchronously the mode
     * lived in a local variable for the few seconds between the body parse and
     * the last write; now the request is gone before the first row lands, and
     * the runner that picks the batch up — possibly on another task, after a
     * deploy, two attempts later — has nowhere else to read it. Defaulting it in
     * the runner would silently turn every SKIP_EXISTING import into an
     * UPDATE_ON_MATCH one, overwriting live records the hauler asked us to leave
     * alone.
     */
    commitMode: text('commit_mode'),
    /**
     * Why a FAILED attempt failed, in the sentence the hauler is shown. Not
     * folded into `error_summary`, which already carries file-level parse
     * problems from upload and the report-upload note from finalize — a third
     * meaning there would make "what went wrong" a question about which key to
     * read.
     */
    commitError: text('commit_error'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('import_batches_tenant_created_idx').on(t.tenantId, t.createdAt.desc()),
    index('import_batches_tenant_status_idx').on(t.tenantId, t.status),
    index('import_batches_tenant_sha_idx')
      .on(t.tenantId, t.fileSha256)
      .where(sql`file_sha256 IS NOT NULL`),
    check('import_batches_source_system_check', sourceSystemCheck),
    check(
      'import_batches_status_check',
      sql.raw(`status IN (${IMPORT_BATCH_STATUSES.map((s) => `'${s}'`).join(', ')})`),
    ),
    // §80 caps the upload at 50 MB. Enforced server-side too, but a row claiming more
    // than that never came from the real path.
    check(
      'import_batches_file_size_check',
      sql`file_size_bytes IS NULL OR (file_size_bytes >= 0 AND file_size_bytes <= 52428800)`,
    ),
    check(
      'import_batches_counts_nonneg',
      sql`total_rows >= 0 AND rows_created >= 0 AND rows_updated >= 0
          AND rows_skipped >= 0 AND rows_failed >= 0`,
    ),
    // UNDONE without an `undone_at` (or the reverse) makes the undo audit unanswerable,
    // which is the one question the column exists for.
    check('import_batches_undone_consistency', sql`(status = 'UNDONE') = (undone_at IS NOT NULL)`),
    check(
      'import_batches_commit_state_check',
      sql.raw(
        `commit_state IS NULL OR commit_state IN (${IMPORT_COMMIT_STATES.map((s) => `'${s}'`).join(
          ', ',
        )})`,
      ),
    ),
    check('import_batches_commit_attempts_nonneg', sql`commit_attempts >= 0`),
    check(
      'import_batches_commit_mode_check',
      sql`commit_mode IS NULL OR commit_mode IN ('UPDATE_ON_MATCH', 'SKIP_EXISTING')`,
    ),
    // A RUNNING claim with no lease is the one inconsistency that breaks
    // recovery: the sweeper decides a claim is abandoned by comparing now()
    // against the lease, so a NULL there leaves the batch neither live nor
    // recoverable — RUNNING forever, picked up by no tick.
    check(
      'import_batches_running_has_lease',
      sql`commit_state <> 'RUNNING' OR commit_lease_expires_at IS NOT NULL`,
    ),
    // The sweeper's only query: claimable batches across every tenant. Partial
    // on `commit_state IS NOT NULL` so it indexes only batches ever claimed —
    // terminal DONE rows accumulate forever and are never what a tick wants —
    // and because 0101's indexes on this table are all `tenant_id`-leading,
    // which a cross-tenant sweep cannot use.
    index('import_batches_commit_state_idx')
      .on(t.commitState, t.commitLeaseExpiresAt)
      .where(sql`commit_state IS NOT NULL`),
    pgPolicy('import_batches_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type ImportBatch = typeof importBatches.$inferSelect;
export type NewImportBatch = typeof importBatches.$inferInsert;

// ── import_rows ─────────────────────────────────────────────────────────────

export const importRows = pgTable(
  'import_rows',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /**
     * CASCADE here is correct, and is the one place it is: staging rows are meaningless
     * without their batch, and discarding a batch should discard its staging.
     */
    batchId: uuid('batch_id')
      .notNull()
      .references(() => importBatches.id, { onDelete: 'cascade' }),
    entityFamily: text('entity_family').notNull(),
    /** NULL for a single-entity CSV, which has no sheet. */
    sheetName: text('sheet_name'),
    /**
     * 1-based row number in the source sheet, so an error can say "Row 7" and mean what
     * the hauler sees. Row 2 is §80's grey example row and is never staged.
     */
    rowNumber: integer('row_number').notNull(),
    /**
     * The hauler's own cells, VERBATIM. Never rewritten — mapping and auto-fix write
     * `mapped` instead, so the preview can show what was typed beside what we made of it.
     * Without that separation an auto-fix is unauditable: the before value is simply gone.
     */
    raw: jsonb('raw').notNull(),
    mapped: jsonb('mapped'),
    severity: text('severity').notNull().default('OK'),
    /**
     * `[{code, field, message, autoFixed, before, after}]` — §80's error / warning /
     * auto-fix taxonomy in one array, so a row's whole story is one read.
     */
    findings: jsonb('findings'),
    /**
     * NULL = not written yet, which is also how a crashed batch resumes: per-row
     * transactions ([[DEC-107]]) plus a persisted per-row outcome means the engine picks
     * up at the first row with no outcome.
     */
    outcome: text('outcome'),
    targetId: uuid('target_id'),
    errorCode: text('error_code'),
    errorDetail: text('error_detail'),
    writtenAt: timestamp('written_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    /**
     * One staged row per (batch, family, sheet, row number). Not hygiene — re-staging a
     * batch must not silently double every row.
     *
     * This constraint does NOT cover the CSV case, where `sheet_name` is NULL: NULLs never
     * conflict in a Postgres unique index, so two rows with the same
     * (batch, family, NULL, 7) would both be accepted. The COALESCE index below closes
     * that hole. Both exist on purpose.
     */
    unique('import_rows_batch_row_key').on(t.batchId, t.entityFamily, t.sheetName, t.rowNumber),
    uniqueIndex('import_rows_batch_row_nullsafe_key').on(
      t.batchId,
      t.entityFamily,
      sql`COALESCE(sheet_name, '')`,
      t.rowNumber,
    ),
    // The preview's filters: all / errors only / warnings only / valid only, per family.
    index('import_rows_batch_family_severity_idx').on(t.batchId, t.entityFamily, t.severity),
    // Resume: the first row with no outcome, in sheet order.
    index('import_rows_batch_pending_idx')
      .on(t.batchId, t.entityFamily, t.rowNumber)
      .where(sql`outcome IS NULL`),
    index('import_rows_tenant_idx').on(t.tenantId),
    check('import_rows_entity_family_check', familyCheck),
    check(
      'import_rows_severity_check',
      sql.raw(`severity IN (${IMPORT_ROW_SEVERITIES.map((s) => `'${s}'`).join(', ')})`),
    ),
    check(
      'import_rows_outcome_check',
      sql.raw(
        `outcome IS NULL OR outcome IN (${IMPORT_ROW_OUTCOMES.map((o) => `'${o}'`).join(', ')})`,
      ),
    ),
    check('import_rows_row_number_check', sql`row_number >= 1`),
    // A FAILED row with no error code is an unanswerable support ticket.
    check(
      'import_rows_failed_has_code',
      sql`outcome IS DISTINCT FROM 'FAILED' OR error_code IS NOT NULL`,
    ),
    pgPolicy('import_rows_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type ImportRow = typeof importRows.$inferSelect;
export type NewImportRow = typeof importRows.$inferInsert;

// ── import_id_maps ──────────────────────────────────────────────────────────

export const importIdMaps = pgTable(
  'import_id_maps',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    /**
     * NOT CASCADE, unlike `import_rows`. The mapping must outlive the batch's staging: it
     * answers "which lodeX row came from this source key?", asked months later by a
     * reconciliation, long after the staging rows were purged for PII reasons. RESTRICT
     * would block that purge, so the reference is nullable and SET NULL.
     */
    batchId: uuid('batch_id').references(() => importBatches.id, { onDelete: 'set null' }),
    entityFamily: text('entity_family').notNull(),
    sourceSystem: text('source_system').notNull().default('SPREADSHEET'),
    /** The legacy PK, or file + sheet + row for a spreadsheet import. */
    sourceKey: text('source_key').notNull(),
    targetTable: text('target_table').notNull(),
    targetId: uuid('target_id').notNull(),
    outcome: text('outcome').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    /**
     * THE IDEMPOTENCY KEY, and the reason this table is load-bearing rather than
     * bookkeeping: it is what makes re-uploading the same file a no-op instead of a
     * duplicate. §80's per-entity natural keys are the other half, but SIX target tables
     * have no unique constraint on their natural key — yards, trailers, hauler_locations,
     * hauler_tanks, requestors, site_contacts (found while deriving the templates in task
     * 20.0) — so the database will not catch those duplicates. This will.
     */
    unique('import_id_maps_source_key').on(t.tenantId, t.sourceSystem, t.entityFamily, t.sourceKey),
    // "What did batch X create?" — the per-batch undo's candidate query. Partial on
    // CREATED because undo only ever removes rows a batch created.
    index('import_id_maps_batch_created_idx')
      .on(t.batchId)
      .where(sql`outcome = 'CREATED'`),
    // The reverse lookup: "where did THIS row come from?"
    index('import_id_maps_target_idx').on(t.tenantId, t.targetTable, t.targetId),
    check('import_id_maps_entity_family_check', familyCheck),
    check('import_id_maps_source_system_check', sourceSystemCheck),
    check(
      'import_id_maps_outcome_check',
      sql.raw(`outcome IN (${IMPORT_ID_MAP_OUTCOMES.map((o) => `'${o}'`).join(', ')})`),
    ),
    check('import_id_maps_source_key_nonempty', sql`length(btrim(source_key)) > 0`),
    pgPolicy('import_id_maps_tenant_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: tenantIsolation,
      withCheck: tenantIsolation,
    }),
  ],
);

export type ImportIdMap = typeof importIdMaps.$inferSelect;
export type NewImportIdMap = typeof importIdMaps.$inferInsert;
